import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import type { Editor } from '@tiptap/react';
import { useCallback, useState } from 'react';

import { ConvertUrlErrorResponse } from '../../components/modals';
import { getEmbedURL, isValidUrl } from '../../utils/urlConverters';

export interface VideoModalReturn {
  isConvertUrlModalOpen: boolean;
  convertUrl: string;
  convertUrlErrors: ConvertUrlErrorResponse;

  openConvertUrlModal: () => void;
  closeConvertUrlModal: () => void;
  setConvertUrl: (url: string) => void;
  setConvertUrlErrors: (errors: ConvertUrlErrorResponse) => void;

  handleSubmitConvertUrl: (url: string) => void;
  insertVideo: (url: string) => void;
}

/**
 * useVideoModal
 * Unified hook for video modal state management + business logic
 */
export function useVideoModal(editor: Editor | null): VideoModalReturn {
  const [isConvertUrlModalOpen, setIsConvertUrlModalOpen] = useState(false);
  const [convertUrl, setConvertUrl] = useState('');
  const [convertUrlErrors, setConvertUrlErrors] = useState<ConvertUrlErrorResponse>({});

  const openConvertUrlModal = useCallback(() => {
    setConvertUrl('');
    setConvertUrlErrors({});
    setIsConvertUrlModalOpen(true);
  }, []);

  const closeConvertUrlModal = useCallback(() => setIsConvertUrlModalOpen(false), []);

  const insertContentWithParagraph = useCallback(
    (insertFn: () => void) => {
      if (!editor) {
        return;
      }

      editor.view.dom.focus({ preventScroll: true });
      insertFn();
      setTimeout(() => {
        const pos = editor.state.selection.$to.pos;
        editor.view.dom.focus({ preventScroll: true });
        editor.chain().insertContentAt(pos, { type: 'paragraph' }).run();
      }, 0);
    },
    [editor],
  );

  const insertVideo = useCallback(
    (url: string) => {
      const embedUrl = getEmbedURL(url);
      if (embedUrl) {
        insertContentWithParagraph(() => {
          editor?.chain().insertIframe({ src: embedUrl }).run();
        });
      }
    },
    [editor, insertContentWithParagraph],
  );

  const handleSubmitConvertUrl = useCallback(
    (url: string) => {
      if (!editor) {
        return;
      }

      if (!url.trim()) {
        setConvertUrlErrors({
          url: [ERROR_MESSAGES.URL.REQUIRED],
        });
        return;
      }

      if (!isValidUrl(url)) {
        setConvertUrlErrors({
          url: [ERROR_MESSAGES.URL.INVALID_FORMAT],
        });
        return;
      }

      const embedUrl = getEmbedURL(url);
      if (embedUrl) {
        insertVideo(url);
        closeConvertUrlModal();
        setConvertUrl('');
        setConvertUrlErrors({});
      } else {
        setConvertUrlErrors({
          url: [ERROR_MESSAGES.URL.INVALID_SERVICE],
        });
      }
    },
    [editor, insertVideo, closeConvertUrlModal],
  );

  return {
    isConvertUrlModalOpen,
    convertUrl,
    convertUrlErrors,

    openConvertUrlModal,
    closeConvertUrlModal,
    setConvertUrl,
    setConvertUrlErrors,

    handleSubmitConvertUrl,
    insertVideo,
  };
}
